import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { trackMemberAreaClick } from "@/lib/analytics";
import { memberAreaUrl } from "@/lib/memberUrl";
import { useLandingData } from "@/context/LandingDataContext";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};

export const Products = () => {
  const { content } = useLandingData();
  const productsSection = content?.productsSection || {
    overline: "— Koleksi Eksklusif",
    title: "Ritual perawatan mewah,",
    italicTitle: "dirancang untuk Anda.",
    description: "Setiap produk DA'VM diformulasikan dengan cermat untuk menghadirkan pengalaman perawatan kulit yang elegan, aman, dan efektif — mulai dari pagi hingga malam hari."
  };
  const products = content?.products || [];

  return (
    <section
      id="produk"
      data-testid="products-section"
      className="relative bg-[#FDFBF7] py-24 md:py-36"
    >
      {/* Section header */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.4 }}
          variants={fadeUp}
          className="max-w-3xl"
        >
          <p className="text-xs tracking-[0.4em] uppercase text-[#BCA38F] mb-6">
            {productsSection.overline}
          </p>
          <h2
            data-testid="products-title"
            className="font-serif-display text-4xl md:text-6xl leading-[1.05] tracking-tight text-[#1A1A1A] font-light"
          >
            {productsSection.title}
            <br />
            <em className="italic font-normal text-[#8A6E52]">
              {productsSection.italicTitle}
            </em>
          </h2>
          <p className="mt-8 text-base md:text-lg text-[#5C5C5C] leading-relaxed max-w-xl">
            {productsSection.description}
          </p>
        </motion.div>

        {/* Product list - alternating asymmetric layout */}
        <div className="mt-20 md:mt-32 space-y-28 md:space-y-40">
          {products.map((p, i) => {
            const isEven = i % 2 === 0;
            return (
              <motion.article
                key={p.id}
                data-testid={`product-card-${p.id}`}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.25 }}
                variants={fadeUp}
                className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center"
              >
                {/* Image */}
                <div
                  className={`md:col-span-7 ${
                    isEven ? "md:order-1" : "md:order-2"
                  }`}
                >
                  <div className="relative overflow-hidden bg-[#F5F2EB] aspect-[4/5] md:aspect-[5/6] group">
                    <img
                      src={p.image}
                      alt={p.name}
                      data-testid={`product-image-${p.id}`}
                      className="w-full h-full object-contain p-8 md:p-16 transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                    <div className="absolute top-6 left-6 text-[10px] tracking-[0.28em] uppercase text-[#1A1A1A]/50 bg-[#FDFBF7]/70 backdrop-blur px-3 py-1.5 rounded-full">
                      {p.category}
                    </div>
                    <div className="absolute bottom-6 right-6 text-xs tracking-[0.28em] uppercase text-[#1A1A1A]/40">
                      {String(i + 1).padStart(2, "0")} / {String(products.length).padStart(2, "0")}
                    </div>
                  </div>
                </div>

                {/* Text */}
                <div
                  className={`md:col-span-5 ${
                    isEven ? "md:order-2" : "md:order-1"
                  }`}
                >
                  <p className="text-xs tracking-[0.32em] uppercase text-[#BCA38F] mb-5">
                    {p.tagline}
                  </p>
                  <h3
                    data-testid={`product-name-${p.id}`}
                    className="font-serif-display text-3xl md:text-5xl leading-[1.05] tracking-tight text-[#1A1A1A] font-light"
                  >
                    {p.name}
                  </h3>
                  <p
                    data-testid={`product-description-${p.id}`}
                    className="mt-6 text-base leading-relaxed text-[#5C5C5C]"
                  >
                    {p.description}
                  </p>

                  <ul className="mt-8 space-y-3">
                    {p.benefits?.map((b, k) => (
                      <li
                        key={k}
                        className="flex items-start gap-3 text-sm text-[#1A1A1A]/80"
                      >
                        <span className="mt-2 h-px w-6 bg-[#BCA38F] shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href={memberAreaUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackMemberAreaClick(`product_${p.id}`)}
                    data-testid={`product-cta-${p.id}`}
                    className="mt-10 inline-flex items-center gap-2 text-xs tracking-[0.28em] uppercase text-[#1A1A1A] border-b border-[#1A1A1A] pb-1 hover:text-[#BCA38F] hover:border-[#BCA38F] transition-colors duration-500 group"
                  >
                    Lihat di Member Area
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
